import { FC, useCallback, useEffect } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AutomationResourceItem } from "./AutomationResourceItem";
import { EmptyFilterAlert } from "./EmptyFilterAlert";
import { AutomationResourceActions } from "./resource-actions/AutomationResourceActions";
import { AutomationResourceFiltersSection } from "./resource-filters/AutomationResourceFiltersSection";
import { ResourceFiltersWithAction } from "@/services/cloudchipr.api";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { automationAccountIdsSelector } from "@/store/automations/selectros/common/fields/automationAccountIdsSelector";
import { setAutomationData } from "@/store/automations/automationsSlice";
import { workflowActionSelector } from "@/store/automations/selectros/workflow/workflowActionSelector";
import { automationTypeSelector } from "@/store/automations/selectros/common/automationTypeSelector";
import { automationResourcesSelector } from "@/store/automations/selectros/common/fields/automationResourcesSelector";
import { getGridResourceDataThunk } from "@/store/resources-grid/thunks/data/getGridResourceDataThunk";
import { generateAutomationFoundResourcesMasterGridId } from "@/components/pages/automations/common/components/found-resources/components/AutomationsFoundResourcesCard";

export interface AutomationResourceItemProps {
  resourceFilters: ResourceFiltersWithAction;
  index: number;
  error: string;
}

export const AutomationResource: FC<AutomationResourceItemProps> = ({
  resourceFilters,
  index,
  error,
}) => {
  const dispatch = useAppDispatch();

  const accountIds = useAppSelector(automationAccountIdsSelector);
  const automationType = useAppSelector(automationTypeSelector);
  const workflowAction = useAppSelector(workflowActionSelector);
  const resources = useAppSelector(automationResourcesSelector);

  const filterChangeHandler = useCallback(
    (filter: ResourceFiltersWithAction[]) => {
      dispatch(setAutomationData({ filter }));
    },
    [dispatch],
  );

  const removeResource = useCallback(() => {
    filterChangeHandler(resources.filter((_, i) => i !== index));
  }, [index, resources, filterChangeHandler]);

  useEffect(() => {
    const masterGridId = generateAutomationFoundResourcesMasterGridId(
      resourceFilters.filter.type,
    );

    dispatch(
      getGridResourceDataThunk({
        masterGridId,
        account_ids: accountIds,
        resourceFilters: resourceFilters.filter,
        resourceType: resourceFilters.filter.type,
      }),
    );
  }, [accountIds, resourceFilters.filter, dispatch]);

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box position="relative" flex={1}>
        <AutomationResourceItem
          index={index}
          onChange={filterChangeHandler}
          filters={resourceFilters.filter}
        />

        <AutomationResourceFiltersSection
          index={index}
          error={error}
          resourceFilters={resourceFilters}
          filterChangeHandler={filterChangeHandler}
        />

        <EmptyFilterAlert
          show={!resourceFilters?.filter?.filter_items?.length}
        />

        {automationType !== "offHours" && workflowAction?.includes("clean") && (
          <AutomationResourceActions resourceFilters={resourceFilters} />
        )}
      </Box>

      {resources.length > 1 && (
        <IconButton onClick={removeResource}>
          <DeleteOutlineIcon />
        </IconButton>
      )}
    </Stack>
  );
};
